import React, { useState, useMemo } from 'react';
import { Building, Car, Calendar, Download, Edit, Navigation, Plus, Trash2, Upload, CheckCircle, AlertTriangle, Wrench, Archive, User, List, MapPin, LogOut, Search, RefreshCw, XCircle, Layers, Shield, ClipboardCheck, Fuel, FileWarning, Gauge } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../api.js';
import { insuranceModuleAPI } from '../../api/insurance.js';
import { vehicleResourceAPI, vehicleInsuranceAPI } from '../../api/vehicles.js';
import { insuranceAPI } from '../../api/insurance.js';
import { buildTrulyInsuredIds } from '../../utils/insuranceStatus.js';
import { invalidateFleetData } from '../../lib/fleetInvalidation.js';
import { reportAPI } from '../../api/reports.js';
import { handleApiError } from '../../utils/errorHandler.js';
import VehicleLifecycleWorkflow from './forms/VehicleLifecycleWorkflow.jsx';
import DriverAssignmentModal from './modals/DriverAssignmentModal.jsx';
import LocationAssignmentModal from './modals/LocationAssignmentModal.jsx';
import VehicleDetailView from './modals/VehicleDetailView.jsx';
import VehicleImportModal from './modals/VehicleImportModal.jsx';
import BulkActionsModal from './modals/BulkActionsModal.jsx';
import BulkInspectionWizard from './resources/BulkInspectionWizard.jsx';
import FleetMapView from '../map/FleetMapView.jsx';
import CalendarView from '../calendar/CalendarView.jsx';
import InsuranceVerificationModal from '../insurance/modals/InsuranceVerificationModal.jsx';
import VehicleIssueDetailModal from './modals/VehicleIssueDetailModal.jsx';
import { PoolPresenceBadge, usePoolCustodyIndex } from '../pool/PoolPresenceCard.jsx';

// New UI Ergonomy components
import QuickFilterChips from '../common/QuickFilterChips.jsx';
import IssueBadge, { calculateVehicleIssues } from '../common/IssueBadge.jsx';
import SkeletonLoader from '../common/SkeletonLoader.jsx';
import ContextualActionsMenu from '../common/ContextualActionsMenu.jsx';
import Pagination, { usePagination } from '../common/Pagination.jsx';

// Navigation context for cross-view navigation
import { useNavigation } from '../../v3/context/NavigationContext.jsx';
import { useSearch } from '../../v3/context/SearchContext.jsx';
import { useProfile } from '../../v3/context/ProfileContext.jsx';

// TODO-007: Extract hooks from index.js (see docs/TODO_ISSUES.md)
import { useVehicles, useDrivers, useOrganizations, useWorkingSites, useTrips, useClaims, useMaintenance, arrayToCSV, downloadCSV } from '../../index.js';

function VehiclesView({ onMessage, returnTo, returnParams, onNavigate }) {
  // Navigation context for cross-view navigation (issue pills + incoming deep links)
  const { t } = useTranslation();
  const { navigateTo, viewParams: navParams, clearParams } = useNavigation();
  const { isTabVisible: isTabVisibleProfile, isReadOnly: profileReadOnly } = useProfile();
  const showListTab = isTabVisibleProfile('vehicles', 'list');
  const showMapTab = isTabVisibleProfile('vehicles', 'map');

  // Use React Query hooks for data fetching
  // Defensive: ensure arrays even if API returns null/object
  const queryClient = useQueryClient();
  const { data: vehiclesRaw = [], isLoading: vehiclesLoading, error: vehiclesError } = useVehicles();
  const { data: driversRaw = [] } = useDrivers();
  const { data: organizationsRaw = [] } = useOrganizations();
  const { data: sitesRaw = [] } = useWorkingSites();
  // Gardes en cours, en UNE lecture pour toute la liste : jamais un appel par
  // ligne. Silencieux si le module pool n'est pas là.
  const { byVehicle: custodyByVehicle } = usePoolCustodyIndex();
  const { data: vehicleLocationsRaw = [] } = useQuery({
    queryKey: ['vehicleLocations', 'current'],
    queryFn: async () => {
      const response = await api.vehicleLocation.getCurrent();
      return response.data || [];
    },
  });
  const { data: tripsRaw = [] } = useTrips();
  const { data: claimsRaw = [] } = useClaims();
  const { data: maintenanceRaw = [] } = useMaintenance();
  const { data: verificationStatusMap = {} } = useQuery({
    queryKey: ['insuranceVerificationBulk'],
    queryFn: async () => {
      try {
        const res = await insuranceModuleAPI.getVerificationSummary();
        if (!res.ok) return {};
        // Also load bulk statuses for per-vehicle badges
        const bulkRes = await insuranceModuleAPI.getUnverifiedVehicles();
        const map = {};
        if (bulkRes.ok && Array.isArray(bulkRes.data)) {
          for (const v of bulkRes.data) {
            map[v.vehicle_id] = { status: v.status, discrepancy_count: 0, vehicle_insurance_id: v.vehicle_insurance_id, contract_id: v.contract_id };
          }
        }
        return map;
      } catch { return {}; }
    },
    staleTime: 60000,
  });
  const { data: insuredVehicleIds = new Set() } = useQuery({
    queryKey: ['trulyInsuredVehicleIdsSet'],
    queryFn: async () => {
      try {
        const [viRes, cRes] = await Promise.all([
          vehicleInsuranceAPI.getAll(),
          insuranceAPI.getAll(),
        ]);
        const vis = Array.isArray(viRes?.data) ? viRes.data : Array.isArray(viRes) ? viRes : [];
        const contracts = Array.isArray(cRes?.data) ? cRes.data : Array.isArray(cRes) ? cRes : [];
        return buildTrulyInsuredIds(vis, contracts);
      } catch { return new Set(); }
    },
    staleTime: 60000,
  });
  const { data: resourceAlertMap = {} } = useQuery({
    queryKey: ['resourceAlertsSummary'],
    queryFn: async () => {
      try {
        const res = await vehicleResourceAPI.getAlertsSummary();
        if (!res.ok) return {};
        const FUEL_RESOURCE_TYPES = new Set(['fuel_level', 'adblue_level', 'oil_level', 'brake_fluid', 'coolant_level', 'washer_fluid']);
        // Value-based severity: ≤15% = critical, ≤30% = warning (matches backend thresholds)
        const isCritical = (a) => (a.value_percent ?? 100) <= 15;
        const isWarning = (a) => { const v = a.value_percent ?? 100; return v > 15 && v <= 30; };
        const map = {};
        for (const v of (res.data?.by_vehicle || [])) {
          const alerts = v.alerts || [];
          const fuelAlerts = alerts.filter(a => FUEL_RESOURCE_TYPES.has(a.resource_type));
          const maintAlerts = alerts.filter(a => !FUEL_RESOURCE_TYPES.has(a.resource_type));
          const fuelAlert = fuelAlerts[0] || null;
          map[v.vehicle_id] = {
            critical: maintAlerts.filter(isCritical).length,
            warning: maintAlerts.filter(isWarning).length,
            fuelCritical: fuelAlerts.filter(isCritical).length,
            fuelWarning: fuelAlerts.filter(a => !isCritical(a)).length,
            fuelAlerts, // full array — one entry per fuel resource type
            fuelAlert,  // first fuel alert (for inline gauge next to mileage)
          };
        }
        return map;
      } catch { return {}; }
    },
    staleTime: 60000,
  });
  const vehicles = Array.isArray(vehiclesRaw) ? vehiclesRaw : [];
  const drivers = Array.isArray(driversRaw) ? driversRaw : [];
  const organizations = Array.isArray(organizationsRaw) ? organizationsRaw : [];
  const sites = Array.isArray(sitesRaw) ? sitesRaw : [];
  const vehicleLocations = Array.isArray(vehicleLocationsRaw) ? vehicleLocationsRaw : [];
  const trips = Array.isArray(tripsRaw) ? tripsRaw : [];
  const claims = Array.isArray(claimsRaw) ? claimsRaw : [];
  const maintenance = Array.isArray(maintenanceRaw) ? maintenanceRaw : [];

  // Map<vehicle_id, { scheduled_date, garage_name }> — earliest *future* planned
  // MR per vehicle. Once scheduled_date is in the past without completion the
  // alert resurfaces in red so a missed appointment doesn't keep the vehicle
  // silenced. See tech_debt_alert_resource_link.md.
  const plannedMaintenanceByVehicle = useMemo(() => {
    const todayYMD = (() => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    const map = new Map();
    for (const m of maintenance) {
      if (m.status !== 'planned' || !m.vehicle_id) continue;
      // Drop appointments whose date is already past — covering window expired.
      if (m.scheduled_date && m.scheduled_date < todayYMD) continue;
      const prev = map.get(m.vehicle_id);
      if (!prev || (m.scheduled_date && (!prev.scheduled_date || m.scheduled_date < prev.scheduled_date))) {
        map.set(m.vehicle_id, {
          scheduled_date: m.scheduled_date,
          garage_name: m.garage_name || null,
        });
      }
    }
    return map;
  }, [maintenance]);

  // UI state (not server data)
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'map'
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [workflowMode, setWorkflowMode] = useState('entry'); // 'entry' or 'exit'
  const [workflowVehicle, setWorkflowVehicle] = useState(null);
  const [showDriverAssignment, setShowDriverAssignment] = useState(false);
  const [showLocationAssignment, setShowLocationAssignment] = useState(false);
  const [assigningVehicle, setAssigningVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showBulkInspection, setShowBulkInspection] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verifyingVehicle, setVerifyingVehicle] = useState(null); // { vehicleId, vehicleInsuranceId }
  const [issueDetailData, setIssueDetailData] = useState(null); // { issue, vehicle } for drill-down modal
  const [orgFilter, setOrgFilter] = useState(''); // '' = all orgs
  const [statusFilters, setStatusFilters] = useState([]); // Quick filter chips
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const { searchQuery } = useSearch();

  // Handle loading state
  const loading = vehiclesLoading;

  // Auto-select vehicle from V3 NavigationContext (deep links, widget clicks, search)
  // Uses a ref + counter to survive the race condition where navParams arrive before vehicles load
  const pendingVehicleIdRef = React.useRef(null);
  const [navTrigger, setNavTrigger] = useState(0);

  // Effect A: Capture incoming navigation params into ref (survives re-renders)
  React.useEffect(() => {
    const selectId = navParams?.selectedId || navParams?.selectVehicleId;
    if (selectId) {
      pendingVehicleIdRef.current = selectId;
      setNavTrigger(t => t + 1); // bump to trigger Effect B even if vehicles didn't change
      clearParams(); // consume context params immediately
    }
  }, [navParams, clearParams]);

  // Effect B: When vehicles load OR nav trigger fires, check ref and open modal
  React.useEffect(() => {
    if (pendingVehicleIdRef.current && vehicles.length > 0) {
      // eslint-disable-next-line eqeqeq
      const vehicle = vehicles.find(v => v.id == pendingVehicleIdRef.current);
      if (vehicle) {
        setSelectedVehicle(vehicle);
        setShowDetailView(true);
        setActiveTab('list'); // switch back to list tab if on map/calendar
        pendingVehicleIdRef.current = null; // consumed
      }
    }
  }, [vehicles, navTrigger]);

  // Handle errors
  if (vehiclesError) {
    onMessage && onMessage({
      title: t('vehicles.toast.error'),
      message: t('vehicles.list.messages.loadError'),
      type: 'error'
    });
  }

  // Refresh function — delegates to the single fleet invalidator so every
  // derived screen (list, badges, locations, issues) stays in sync.
  const handleRefresh = () => {
    invalidateFleetData(queryClient);
  };

  const handleWorkflowSuccess = () => {
    invalidateFleetData(queryClient);
    setWorkflowVehicle(null);
  };

  const handleAssignmentSuccess = () => {
    invalidateFleetData(queryClient);
    setAssigningVehicle(null);
  };

  const handleEdit = (vehicle) => {
    setWorkflowVehicle(vehicle);
    setWorkflowMode('entry');
    setShowWorkflow(true);
  };

  const handleExitVehicle = (vehicle) => {
    // Cannot exit a vehicle that's already retired
    if (vehicle.status === 'retired') {
      onMessage && onMessage({
        title: t('vehicles.list.messages.alreadyRetiredTitle'),
        message: t('vehicles.list.messages.alreadyRetired'),
        type: 'warning'
      });
      return;
    }
    setWorkflowVehicle(vehicle);
    setWorkflowMode('exit');
    setShowWorkflow(true);
  };

  const handleCloseWorkflow = () => {
    setShowWorkflow(false);
    setWorkflowVehicle(null);
    setWorkflowMode('entry');
  };

  const handleAddVehicle = () => {
    setWorkflowVehicle(null);
    setWorkflowMode('entry');
    setShowWorkflow(true);
  };

  const handleAssignDriver = (vehicle) => {
    setAssigningVehicle(vehicle);
    setShowDriverAssignment(true);
  };

  const handleAssignLocation = (vehicle) => {
    setAssigningVehicle(vehicle);
    setShowLocationAssignment(true);
  };

  const handleCloseDriverAssignment = () => {
    setShowDriverAssignment(false);
    setAssigningVehicle(null);
  };

  const handleCloseLocationAssignment = () => {
    setShowLocationAssignment(false);
    setAssigningVehicle(null);
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailView(true);
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedVehicle(null);
    // Navigate back to returnTo view if specified, with optional params to restore context
    if (returnTo && onNavigate) {
      onNavigate(returnTo, returnParams || {});
    }
  };

  const handleDetailViewUpdate = () => {
    invalidateFleetData(queryClient);
  };

  // Handle issue pill clicks - navigate to relevant workflow
  // When expandGroup is true, open the drill-down modal instead of navigating
  const handleIssueClick = (issue, vehicle) => {
    // Grouped issues: open drill-down modal
    if (issue.expandGroup && issue.itemCount > 1) {
      setIssueDetailData({ issue, vehicle });
      return;
    }

    switch (issue.type) {
      case 'claim_open':
        if (issue.data?.id) {
          navigateTo('claims', { selectClaimId: issue.data.id });
        } else {
          navigateTo('claims');
        }
        break;
      case 'maintenance_overdue':
      case 'maintenance_due':
        if (issue.data?.id) {
          navigateTo('maintenance', { selectMaintenanceId: issue.data.id });
        } else {
          navigateTo('maintenance');
        }
        break;
      case 'ct_expired':
      case 'ct_expiring':
        navigateTo('inspections');
        break;
      case 'insurance_expired':
      case 'insurance_expiring':
      case 'insurance_missing':
        navigateTo('insurance');
        break;
      case 'insurance_not_verified':
      case 'insurance_discrepancy':
        if (issue.data?.vehicle_insurance_id) {
          setVerifyingVehicle({
            vehicleId: issue.data.vehicleId,
            vehicleInsuranceId: issue.data.vehicle_insurance_id,
            contractId: issue.data.contract_id,
          });
          setShowVerificationModal(true);
        } else {
          navigateTo('insurance');
        }
        break;
      case 'resource_critical':
      case 'resource_warning':
        // Show detail modal for maintenance resource issues (brakes, tires, oil...)
        setIssueDetailData({ issue: { ...issue, expandGroup: true }, vehicle });
        break;
      case 'fuel_low':
        // Fuel/energy alerts → go to fuel workflow with vehicle + fuel type pre-selected
        navigateTo('fuel', {
          createForVehicleId: vehicle.id,
          vehiclePlate: vehicle.license_plate,
          defaultFuelType: issue.data?.fuelType || null,
          returnTo: 'vehicles'
        });
        break;
      case 'immobilized':
        break;
      default:
        console.log('Issue clicked:', issue);
    }
  };

  // Handle clicking an individual item inside the drill-down modal
  const handleIssueDetailItemClick = (issueType, item) => {
    setIssueDetailData(null); // close drill-down modal
    switch (issueType) {
      case 'claim_open':
        navigateTo('claims', { selectClaimId: item.id });
        break;
      case 'maintenance_overdue':
      case 'maintenance_due':
        navigateTo('maintenance', { selectMaintenanceId: item.id });
        break;
      case 'resource_critical':
      case 'resource_warning': {
        // Fluids → fuel/consumable top-up form, wear/hull → maintenance
        const FLUID_TYPES = new Set(['fuel_level', 'adblue_level', 'oil_level', 'brake_fluid', 'coolant_level', 'washer_fluid']);
        const vehicleId = item.vehicle_id || issueDetailData?.vehicle?.id;
        if (item.resource_type && FLUID_TYPES.has(item.resource_type)) {
          const fuelTypeMap = { fuel_level: null, adblue_level: 'adblue', oil_level: 'engine_oil', brake_fluid: 'brake_fluid', coolant_level: 'coolant', washer_fluid: 'washer_fluid' };
          navigateTo('fuel', {
            createForVehicleId: vehicleId,
            defaultFuelType: fuelTypeMap[item.resource_type] || null,
          });
        } else {
          navigateTo('maintenance', { createForVehicleId: vehicleId });
        }
        break;
      }
      default:
        break;
    }
  };

  // Helper function to get current location for a vehicle
  const getCurrentLocation = (vehicleId) => {
    const location = vehicleLocations.find(loc => loc.vehicle_id === vehicleId && loc.is_current);
    if (location) {
      const site = sites.find(s => s.id === location.site_id);
      return site ? site.site_name : t('vehicles.list.unknownSite');
    }
    return t('vehicles.list.notAssigned');
  };

  // Helper function to get assigned driver
  const _getAssignedDriver = (vehicleId) => {
    // First check if vehicle has an assigned driver
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle && vehicle.assigned_driver_id) {
      const driver = drivers.find(d => d.id === vehicle.assigned_driver_id);
      return driver ? `${driver.first_name} ${driver.last_name}` : t('vehicles.list.unknownDriver');
    }

    // Fallback: check active or planned trip for this vehicle
    const activeTrip = trips.find(t =>
      t.vehicle_id === vehicleId && (t.status === 'in_progress' || t.status === 'planned')
    );
    if (activeTrip) {
      const driver = drivers.find(d => d.id === activeTrip.driver_id);
      return driver ? `${driver.first_name} ${driver.last_name}` : t('vehicles.list.unknownDriver');
    }
    return t('vehicles.list.notAssigned');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('vehicles.list.messages.deleteConfirm'))) return;

    try {
      await api.vehicle.delete(id);
      onMessage && onMessage({
        title: t('vehicles.toast.success'),
        message: t('vehicles.list.messages.deleted'),
        type: 'success'
      });
      invalidateFleetData(queryClient);
    } catch (error) {
      handleApiError(error, 'delete', { onMessage });
    }
  };

  const handleExportCSV = () => {
    const columns = [
      { key: 'id', label: t('vehicles.list.csv.id') },
      { key: 'make', label: t('vehicles.list.csv.make') },
      { key: 'model', label: t('vehicles.list.csv.model') },
      { key: 'year', label: t('vehicles.list.csv.year') },
      { key: 'vin', label: t('vehicles.list.csv.vin') },
      { key: 'license_plate', label: t('vehicles.list.csv.plate') },
      { key: 'status', label: t('vehicles.list.csv.status') },
      { key: 'mileage', label: t('vehicles.list.csv.mileage') },
      { key: 'fuel_type', label: t('vehicles.list.csv.fuelType') },
      { key: 'acquisition_type', label: t('vehicles.list.csv.acquisitionType') },
      { key: 'purchase_date', label: t('vehicles.list.csv.purchaseDate') },
      { key: 'purchase_price', label: t('vehicles.list.csv.purchasePrice') },
    ];

    const csvContent = arrayToCSV(vehicles, columns);
    const filename = `vehicles_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);

    onMessage && onMessage({
      title: t('vehicles.toast.success'),
      message: t('vehicles.list.messages.exportDone', { count: vehicles.length }),
      type: 'success'
    });
  };

  // Generate fleet resource snapshot report and auto-download PDF
  const handleResourceSnapshot = async () => {
    setSnapshotLoading(true);
    try {
      const res = await reportAPI.generate('fleet_resource_snapshot', { format: 'pdf' });
      if (res?.ok && res?.data?.id) {
        await reportAPI.download(res.data.id, 'pdf');
        onMessage?.({ title: t('vehicles.list.messages.snapshotTitle'), message: t('vehicles.list.messages.snapshotDone'), type: 'success' });
      } else {
        onMessage?.({ title: t('vehicles.toast.error'), message: res?.error?.message || t('vehicles.list.messages.snapshotFailed'), type: 'error' });
      }
    } catch (err) {
      onMessage?.({ title: t('vehicles.toast.error'), message: err.message || t('vehicles.list.messages.snapshotError'), type: 'error' });
    } finally {
      setSnapshotLoading(false);
    }
  };

  // Calculate status counts for filter chips
  const statusCounts = vehicles.reduce((acc, v) => {
    acc[v.status] = (acc[v.status] || 0) + 1;
    return acc;
  }, {});

  // Filter vehicles by search, organization and status (must be before early return)
  const filteredVehicles = useMemo(() => {
    let result = vehicles;

    // Apply search filter
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      result = result.filter(v =>
        v.license_plate?.toLowerCase().includes(query) ||
        v.make?.toLowerCase().includes(query) ||
        v.model?.toLowerCase().includes(query) ||
        v.vin?.toLowerCase().includes(query) ||
        v.org_id?.toLowerCase().includes(query)
      );
    }

    // Apply org filter
    if (orgFilter) {
      result = result.filter(v => v.org_id === orgFilter);
    }

    // Enrich vehicles with resource alerts BEFORE filtering (so calculateVehicleIssues sees them)
    result = result.map(v => {
      const alerts = resourceAlertMap[v.id];
      if (alerts && (alerts.critical > 0 || alerts.warning > 0 || alerts.fuelCritical > 0 || alerts.fuelWarning > 0)) {
        return { ...v, _resourceAlerts: alerts };
      }
      return v;
    });

    // Apply status filters
    if (statusFilters.length > 0) {
      result = result.filter(v => {
        // Check for 'issues' special filter
        if (statusFilters.includes('issues')) {
          const issues = calculateVehicleIssues(v, { claims, maintenance, insuranceVerification: verificationStatusMap[v.id], insuredVehicleIds, plannedMaintenanceByVehicle });
          if (issues.length > 0) return true;
        }
        // Check normal status filters
        return statusFilters.includes(v.status);
      });
    } else {
      // By default, exclude retired vehicles from the main view
      // Users must explicitly click "Retiré" filter to see archived vehicles
      result = result.filter(v => v.status !== 'retired');
    }

    return result;
  }, [vehicles, searchQuery, orgFilter, statusFilters, claims, maintenance, verificationStatusMap, resourceAlertMap, insuredVehicleIds, plannedMaintenanceByVehicle]);

  const { currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, paginateArray } = usePagination(filteredVehicles.length, 1, 25);

  // Show skeleton loader while loading
  if (loading) return <SkeletonLoader type="table" rows={8} columns={7} />;

  // Calculate issue counts (enrich with resource alerts for accurate count)
  const vehiclesWithIssues = vehicles.filter(v => {
    const enriched = resourceAlertMap[v.id] ? { ...v, _resourceAlerts: resourceAlertMap[v.id] } : v;
    const issues = calculateVehicleIssues(enriched, { claims, maintenance, insuranceVerification: verificationStatusMap[v.id], insuredVehicleIds, plannedMaintenanceByVehicle });
    return issues.length > 0;
  }).length;

  // Define quick filter chips
  const filterChips = [
    {
      key: 'active',
      label: t('vehicles.list.filters.active'),
      count: statusCounts['active'] || 0,
      icon: CheckCircle,
      activeColor: 'bg-green-500 text-white',
      inactiveColor: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
    },
    {
      key: 'maintenance',
      label: t('vehicles.list.filters.maintenance'),
      count: statusCounts['maintenance'] || 0,
      icon: Wrench,
      activeColor: 'bg-amber-500 text-white',
      inactiveColor: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
    },
    {
      key: 'issues',
      label: t('vehicles.list.filters.issues'),
      count: vehiclesWithIssues,
      icon: AlertTriangle,
      activeColor: 'bg-red-500 text-white',
      inactiveColor: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
    },
    {
      key: 'retired',
      label: t('vehicles.list.filters.retired'),
      count: statusCounts['retired'] || 0,
      icon: Archive,
      activeColor: 'bg-gray-500 text-white',
      inactiveColor: 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
    }
  ];

  return (
    <div className="space-y-4">
      <VehicleLifecycleWorkflow
        isOpen={showWorkflow}
        onClose={handleCloseWorkflow}
        onSuccess={handleWorkflowSuccess}
        onMessage={onMessage}
        mode={workflowMode}
        vehicle={workflowVehicle}
        onStartInspection={(v) => navigateTo('inspections', { createForVehicleId: v.id, vehiclePlate: v.license_plate })}
      />

      <DriverAssignmentModal
        isOpen={showDriverAssignment}
        onClose={handleCloseDriverAssignment}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        vehicle={assigningVehicle}
        drivers={drivers}
      />

      <LocationAssignmentModal
        isOpen={showLocationAssignment}
        onClose={handleCloseLocationAssignment}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        vehicle={assigningVehicle}
        sites={sites}
      />

      <VehicleImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onSuccess={() => {
          invalidateFleetData(queryClient);
          setShowImport(false);
        }}
        onMessage={onMessage}
      />

      {showBulkActions && (
        <BulkActionsModal
          onClose={() => setShowBulkActions(false)}
          onSuccess={() => invalidateFleetData(queryClient)}
          onMessage={onMessage}
          vehicles={vehicles}
          drivers={drivers}
          sites={sites}
          onOpenInspection={() => setShowBulkInspection(true)}
        />
      )}

      {showBulkInspection && (
        <BulkInspectionWizard
          isOpen={showBulkInspection}
          onClose={() => setShowBulkInspection(false)}
          onSuccess={() => {
            setShowBulkInspection(false);
            invalidateFleetData(queryClient);
          }}
          onMessage={onMessage}
        />
      )}

      {showDetailView && (
        <VehicleDetailView
          vehicle={selectedVehicle}
          onClose={handleCloseDetailView}
          onMessage={onMessage}
          onUpdate={handleDetailViewUpdate}
        />
      )}

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Car className="w-6 h-6 text-amber-600" />
              {t('vehicles.title')}
            </h2>
            <p className="text-gray-600 mt-1 flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{t('vehicles.list.activeCount', { count: vehicles.filter(v => v.status !== 'retired').length })}</span>
              <span className="text-gray-300">|</span>
              <span className="text-green-600">{t('vehicles.list.available', { count: statusCounts['active'] || 0 })}</span>
              {statusCounts['maintenance'] > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-amber-600">{t('vehicles.list.inMaintenance', { count: statusCounts['maintenance'] })}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
              disabled={vehicles.length === 0}
            >
              <Download className="w-5 h-5" />
              {t('vehicles.list.exportCsv')}
            </button>
            <button
              onClick={handleResourceSnapshot}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center gap-2"
              disabled={snapshotLoading}
              title={t('vehicles.list.resourceSnapshotTitle')}
            >
              <Gauge className={`w-5 h-5 ${snapshotLoading ? 'animate-spin' : ''}`} />
              {t('vehicles.list.resourceSnapshot')}
            </button>
            {!profileReadOnly && (
              <button
                onClick={() => setShowImport(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
              >
                <Upload className="w-5 h-5" />
                {t('common.actions.import')}
              </button>
            )}
            {!profileReadOnly && (
              <button
                onClick={() => setShowBulkActions(true)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2"
              >
                <Layers className="w-5 h-5" />
                {t('vehicles.list.bulkActions')}
              </button>
            )}
            {!profileReadOnly && (
              <button
                onClick={handleAddVehicle}
                data-action="add-vehicle"
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                {t('vehicles.list.addVehicle')}
              </button>
            )}
          </div>
        </div>

        {/* Filters and Refresh */}
        <div className="flex gap-4 mb-4">
          <select
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">{t('vehicles.list.allOrganizations')}</option>
            {organizations.map(org => (
              <option key={org.id} value={org.code}>
                {org.name} ({org.code})
              </option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tabs and Filters */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            {showListTab && (
              <button
                onClick={() => setActiveTab('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'list'
                    ? 'bg-amber-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-4 h-4" />
                {t('vehicles.list.tabs.list')}
              </button>
            )}
            {showMapTab && (
              <button
                onClick={() => setActiveTab('map')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'map'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MapPin className="w-4 h-4" />
                {t('vehicles.list.tabs.map')}
              </button>
            )}
            <button
              onClick={() => setActiveTab('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'calendar'
                  ? 'bg-amber-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              {t('vehicles.list.tabs.calendar')}
            </button>
            <div className="h-6 w-px bg-gray-200 mx-2" />
            <QuickFilterChips
              filters={filterChips.filter(f => f.key !== 'retired')}
              activeFilters={statusFilters}
              onFilterChange={setStatusFilters}
              multiSelect={true}
              totalCount={vehicles.filter(v => v.status !== 'retired').length}
            />
          </div>
          <div className="flex items-center gap-2">
            {(orgFilter || statusFilters.length > 0) && (
              <button
                onClick={() => { setOrgFilter(''); setStatusFilters([]); }}
                className="text-sm text-amber-600 hover:text-amber-700 underline"
              >
                {t('vehicles.list.reset')}
              </button>
            )}
            <span className="text-sm text-gray-600">
              {t('vehicles.list.shown', { count: filteredVehicles.length })}
            </span>
          </div>
        </div>
      </div>

      {activeTab === 'list' ? (
        <div key="vehicle-list-view">

      {filteredVehicles.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {searchQuery || orgFilter || statusFilters.length > 0 ? t('common.states.noResult') : t('vehicles.list.emptyTitle')}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || orgFilter || statusFilters.length > 0
              ? t('vehicles.list.noResultHint')
              : t('vehicles.list.emptyHint')}
          </p>
          {!(searchQuery || orgFilter || statusFilters.length > 0) && !profileReadOnly && (
            <button
              onClick={handleAddVehicle}
              data-action="add-vehicle"
              className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
            >
              {t('vehicles.list.addVehicle')}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.vehicle')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.plate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.organization')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.issues')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.location')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.mileage')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('vehicles.list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginateArray(filteredVehicles).map(vehicle => {
                const vehicleIssues = calculateVehicleIssues(vehicle, { claims, maintenance, insuranceVerification: verificationStatusMap[vehicle.id], insuredVehicleIds, plannedMaintenanceByVehicle });
                return (
                <tr
                  key={vehicle.id}
                  onClick={() => handleViewDetails(vehicle)}
                  className="hover:bg-amber-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{vehicle.make} {vehicle.model}</span>
                      <PoolPresenceBadge kind="vehicle" custody={custodyByVehicle.get(String(vehicle.id))} />
                    </div>
                    <div className="text-sm text-gray-500">{vehicle.year}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{vehicle.license_plate}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {(() => {
                          const org = organizations.find(o => o.code === vehicle.org_id);
                          return org ? org.name : (vehicle.org_id || <span className="text-gray-400 italic">{t('vehicles.list.unassignedOrg')}</span>);
                        })()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      vehicle.status === 'active' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'maintenance' ? 'bg-amber-100 text-amber-800' :
                      vehicle.status === 'retired' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {t(`vehicles.status.${vehicle.status}`, { defaultValue: vehicle.status })}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {vehicleIssues.length > 0 ? (
                      <IssueBadge
                        issues={vehicleIssues}
                        onIssueClick={(issue) => handleIssueClick(issue, vehicle)}
                      />
                    ) : (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> {t('vehicles.list.ok')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{getCurrentLocation(vehicle.id)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <span>{vehicle.mileage?.toLocaleString() || 0} km</span>
                      {vehicle._resourceAlerts?.fuelAlert && (
                        <span title={`${t(`vehicles.fluids.${vehicle._resourceAlerts.fuelAlert.resource_type}`, { defaultValue: t('vehicles.fluids.default') })}: ${vehicle._resourceAlerts.fuelAlert.value_percent}%`}
                          className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                            vehicle._resourceAlerts.fuelAlert.status === 'critical' || vehicle._resourceAlerts.fuelAlert.status === 'replace'
                              ? 'text-red-600' : 'text-amber-600'
                          }`}>
                          <Fuel className="w-3.5 h-3.5" />
                          {vehicle._resourceAlerts.fuelAlert.value_percent}%
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    {!profileReadOnly && (
                      <ContextualActionsMenu
                        actions={[
                          {
                            key: 'edit',
                            label: t('common.actions.edit'),
                            icon: Edit,
                            onClick: () => handleEdit(vehicle)
                          },
                          {
                            key: 'assign-location',
                            label: t('vehicles.list.actions.assignLocation'),
                            icon: Navigation,
                            onClick: () => handleAssignLocation(vehicle)
                          },
                          {
                            key: 'assign-driver',
                            label: t('vehicles.list.actions.assignDriver'),
                            icon: User,
                            onClick: () => handleAssignDriver(vehicle)
                          },
                          { divider: true },
                          {
                            key: 'declare-claim',
                            label: t('vehicles.list.actions.declareClaim'),
                            icon: Shield,
                            onClick: () => navigateTo('claims', { createForVehicleId: vehicle.id, vehiclePlate: vehicle.license_plate })
                          },
                          { divider: true },
                          // f(X) philosophy: user is on vehicle X, picks the action, lands
                          // on the destination tab with X pre-filled. Flat list (no nested
                          // submenu) keeps everything 1-click and dodges clipping/flyout
                          // edge cases. returnTo='vehicles' on all 4 so the user comes back
                          // to the fleet list after creation.
                          { group: t('vehicles.list.actions.createGroup') },
                          {
                            key: 'op-maintenance',
                            label: t('vehicles.list.actions.maintenance'),
                            icon: Wrench,
                            onClick: () => navigateTo('maintenance', { createForVehicleId: vehicle.id, vehiclePlate: vehicle.license_plate, returnTo: 'vehicles' })
                          },
                          {
                            key: 'op-inspection',
                            label: t('vehicles.list.actions.inspection'),
                            icon: ClipboardCheck,
                            onClick: () => navigateTo('inspections', { createForVehicleId: vehicle.id, vehiclePlate: vehicle.license_plate, returnTo: 'vehicles' })
                          },
                          {
                            key: 'op-fuel',
                            label: t('vehicles.list.actions.fuel'),
                            icon: Fuel,
                            onClick: () => navigateTo('fuel', { createForVehicleId: vehicle.id, vehiclePlate: vehicle.license_plate, returnTo: 'vehicles' })
                          },
                          {
                            key: 'op-fine',
                            label: t('vehicles.list.actions.fine'),
                            icon: FileWarning,
                            onClick: () => navigateTo('fines', { createForVehicleId: vehicle.id, vehiclePlate: vehicle.license_plate, returnTo: 'vehicles' })
                          },
                          { divider: true },
                          {
                            key: 'exit',
                            label: t('vehicles.list.actions.exitFleet'),
                            icon: LogOut,
                            danger: true,
                            disabled: vehicle.status === 'retired',
                            onClick: () => handleExitVehicle(vehicle)
                          },
                          {
                            key: 'delete',
                            label: t('common.actions.delete'),
                            icon: Trash2,
                            danger: true,
                            onClick: () => handleDelete(vehicle.id)
                          }
                        ]}
                      />
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredVehicles.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            className="px-4 py-3 border-t bg-gray-50"
          />
        </div>
      )}
        </div>
      ) : activeTab === 'map' ? (
        /* Map View Tab */
        <FleetMapView key="fleet-map-view" onMessage={onMessage} />
      ) : (
        /* Calendar View Tab */
        <CalendarView key="fleet-calendar-view" onMessage={onMessage} />
      )}

      {/* Insurance Verification Modal — opened from issue pill click */}
      {showVerificationModal && verifyingVehicle && (
        <InsuranceVerificationModal
          isOpen={showVerificationModal}
          onClose={() => { setShowVerificationModal(false); setVerifyingVehicle(null); }}
          vehicleId={verifyingVehicle.vehicleId}
          vehicleInsuranceId={verifyingVehicle.vehicleInsuranceId}
          contractId={verifyingVehicle.contractId}
          onVerified={() => {
            onMessage && onMessage({ title: t('vehicles.toast.success'), message: t('vehicles.list.messages.verificationSaved'), type: 'success' });
            queryClient.invalidateQueries({ queryKey: ['insuranceVerificationBulk'] });
          }}
        />
      )}

      {/* Issue Detail Drill-Down Modal — opened when clicking grouped issue pills */}
      {issueDetailData && (
        <VehicleIssueDetailModal
          issue={issueDetailData.issue}
          vehicle={issueDetailData.vehicle}
          onClose={() => setIssueDetailData(null)}
          onItemClick={handleIssueDetailItemClick}
        />
      )}
    </div>
  );
}

export default VehiclesView;
